<script setup lang="ts">
import { FilterButtonIcon } from '~/interfaces/UI'

export interface FilterButtonProps {
    size?: string
    icon: FilterButtonIcon
    iconColor?: string
    textSingular: string
    textPlural: string
    number: number
    isClicked: boolean
}

const props = withDefaults(defineProps<FilterButtonProps>(), {
    size: 'sm',
    icon: FilterButtonIcon.IconComment,
    iconColor: 'text-gray-l-500',
    textSingular: '',
    textPlural: '',
    number: 0,
    isClicked: false
})

const isDisabled = computed(() => props.number === 0)

const getNumberColorClass = computed(() => {
    if (isDisabled.value) {
        return 'text-gray-300'
    }

    return props.isClicked ? 'text-primary-info' : 'text-gray-l-800'
})

const getTextColorClass = computed(() => {
    if (isDisabled.value) {
        return 'text-gray-300'
    }

    return props.isClicked ? 'text-secondary-info' : 'text-gray-l-500'
})

const getCursorClass = computed(() =>
    isDisabled.value ? 'hover:cursor-not-allowed' : 'hover:cursor-pointer'
)

const getClickedClasses = computed(() =>
    !isDisabled.value && props.isClicked
        ? 'border-2 border-primary-light bg-container-primary-background-selected'
        : 'border'
)

const getText = computed(() =>
    props.number > 1 ? props.textPlural : props.textSingular
)

const getIconColor = computed(() =>
    isDisabled.value ? 'text-gray-l-300' : (props.iconColor as string)
)
</script>

<template>
    <button
        class="flex flex-col gap-1 p-2 sm:p-4 text-left border-solid border-gray-200 rounded-lg box-border w-full transition ease-out duration-200"
        :class="[getCursorClass, getClickedClasses]"
    >
        <span class="flex gap-2 items-center">
            <component
                :is="FilterButtonIcon[icon]"
                class="w-6 sm:w-8"
                :is-disabled
                :class="getIconColor"
            />
            <span
                class="font-bold text-28 tracking-light"
                :class="[getNumberColorClass]"
            >
                {{ number }}
            </span>
        </span>
        <span
            class="w-fit font-normal text-thirteen-exploitation leading-4 tracking-light"
            :class="[getTextColorClass]"
        >
            {{ getText }}
        </span>
    </button>
</template>
